/**
 * 收藏列表状态管理
 * 使用 Zustand 实现布局模式和排序的状态持久化
 */

import { create } from "zustand";

import { STORAGE_KEYS } from "@/shared/constants/storage-keys";
import { storage } from "@/shared/lib/storage";

import type { ItemSortBy, ItemSortOrder, LayoutMode } from "./types";

export type SortBy = ItemSortBy;
export type SortOrder = ItemSortOrder;

const SUPPORTED_SORT_BY: SortBy[] = ["id", "views"];
const SUPPORTED_SORT_ORDER: SortOrder[] = ["asc", "desc"];

function getInitialSortBy(): SortBy {
  const stored = storage.getItem(STORAGE_KEYS.itemSort) as SortBy | null;
  return stored && SUPPORTED_SORT_BY.includes(stored) ? stored : "id";
}

function getInitialSortOrder(): SortOrder {
  const stored = storage.getItem(STORAGE_KEYS.itemSortOrder) as SortOrder | null;
  return stored && SUPPORTED_SORT_ORDER.includes(stored) ? stored : "desc";
}

// 状态接口定义
interface ItemState {
  layoutMode: LayoutMode; // 当前布局模式（card/list/headline/waterfall）
  sortBy: SortBy; // 排序字段
  sortOrder: SortOrder; // 排序方向
  isPreviewOpen: boolean; // 是否打开预览面板
  selectedItemId: string | null;
  actions: {
    setLayoutMode: (layoutMode: LayoutMode) => void;
    setSortBy: (sortBy: SortBy) => void;
    toggleSortOrder: () => void; // 切换排序方向（asc ⟷ desc）
    openPreview: (itemId: string) => void;
    closePreview: () => void;
    setSelectedItemId: (itemId: string | null) => void;
  };
}

// 创建store，自动持久化到localStorage
export const useItemStore = create<ItemState>()(
  (set) => ({
    // 默认值：从 localStorage 读取，或使用默认配置
    layoutMode: (storage.getItem(STORAGE_KEYS.itemLayout) as LayoutMode) || "card",
    sortBy: getInitialSortBy(),
    sortOrder: getInitialSortOrder(),
    isPreviewOpen: false,
    selectedItemId: null,
    actions: {
      // 设置布局模式
      setLayoutMode: (layoutMode: LayoutMode) => {
        set({ layoutMode });
        storage.setItem(STORAGE_KEYS.itemLayout, layoutMode);
      },
      // 设置排序字段
      setSortBy: (sortBy: SortBy) => {
        const nextSortBy = SUPPORTED_SORT_BY.includes(sortBy) ? sortBy : "id";
        set({ sortBy: nextSortBy });
        storage.setItem(STORAGE_KEYS.itemSort, nextSortBy);
      },
      // 切换排序方向（升序 ⟷ 降序）
      toggleSortOrder: () => {
        set((state) => {
          const newOrder = state.sortOrder === "asc" ? "desc" : "asc";
          storage.setItem(STORAGE_KEYS.itemSortOrder, newOrder);
          return { sortOrder: newOrder };
        });
      },
      openPreview: (itemId: string) => {
        set({ isPreviewOpen: true, selectedItemId: itemId });
      },
      closePreview: () => {
        set({ isPreviewOpen: false, selectedItemId: null });
      },
      setSelectedItemId: (itemId: string | null) => {
        set({ selectedItemId: itemId });
      },
    },
  }),
);

// 便捷的Hooks导出
export const useLayoutMode = () => useItemStore((state) => state.layoutMode)
export const useSortBy = () => useItemStore((state) => state.sortBy)
export const useSortOrder = () => useItemStore((state) => state.sortOrder)

export const useIsPreviewOpen = () => useItemStore((state) => state.isPreviewOpen)
export const useSelectedItemId = () => useItemStore((state) => state.selectedItemId)

// 便捷的Actions导出
export const useItemActions = () => useItemStore((state) => state.actions)
